import type {
    IDocumentResponse,
    IFile,
    IFileResponse,
    IGraphData,
    IProjectDetail,
} from '@/domains/project/types';
import { I18nService } from '@/services/i18n-service';
import { SaveToProjectApi } from '@eureka/shared/save-to-project/services';
import { Message } from '@patsnap/tahiti-core';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { DetailApi } from '../apis/detail-api';

export interface DetailService {
    getProjectDetail(projectId: string): Promise<IProjectDetail>;
    getKG(projectId: string): Promise<IGraphData | undefined>;
    getMoreKG(projectId: string, nodeId: string, nodeName: string): Promise<IGraphData | undefined>;
    getRelation(projectId: string, nodeId: string, nodeName: string): Promise<string[] | undefined>;
    getDocuments(projectId: string, nodeIds: string[], page: number, limit: number): Promise<IDocumentResponse>;
    deleteDocument(projectId: string, documentId: string): Promise<void>;
    getSeekQuestions(projectId: string, nodeId: string, nodeName: string): Promise<string[]>;
    getFiles(projectId: string, nodeIds: string[], page: number, limit: number): Promise<IFileResponse>;
    deleteFile(projectId: string, fileId: string): Promise<void>;
    batchDeleteFile(projectId: string, fileIds: string[]): Promise<void>;
    editFile(projectId: string, fileId: string, fileName: string): Promise<void>;
    refreshFile(projectId: string, fileId: string, retryType: 'agent' | 'upload', contentFormatType?: string): Promise<void>;
    getFilesStatus(projectId: string, fileIds: string[]): Promise<IFile[]>;
    uploadFile(projectId: string, file: File): Promise<{ item_id: string }>;
    getExamples(projectId: string): Promise<string[]>;
    getPdfPath(itemId: string, projectId: string): Promise<string>;
    getLsDocumentAgentData(data: {business_id: string, business_type: 'ls_file' | 'ls_file_chat' | 'RD' | 'IP' | 'MSE' | 'LS', item_ids: string[], work_project_id: string}): Promise<{chat_id: string, file_id: string}>;
}

export const DetailService = Symbol('DetailService');

@injectable()
export class DetailServiceImpl implements DetailService {
    constructor(
      @inject(DetailApi) private readonly _detailApi: DetailApi,
      @inject(SaveToProjectApi) private readonly _SaveToProjectApi: SaveToProjectApi,
      @inject(Message) private readonly _message: Message,
      @inject(I18nService) private readonly _i18nService: I18nService
    ) {
    }

    getProjectDetail(projectId: string): Promise<IProjectDetail> {
        return this._detailApi.getProjectDetail(projectId).then((projectDetail) => {
            return projectDetail;
        });
    }

    getKG(projectId: string): Promise<IGraphData | undefined> {
        return this._detailApi.getKG(projectId).then((graphData) => {
            return graphData;
        });
    }

    getMoreKG(projectId: string, nodeId: string, nodeName: string): Promise<IGraphData | undefined> {
        return this._detailApi.getMoreKG(projectId, nodeId, nodeName).then((graphData) => {
            return graphData;
        });
    }

    getRelation(projectId: string, nodeId: string, nodeName: string): Promise<string[] | undefined> {
        return this._detailApi.getRelationNode(projectId, nodeId, nodeName).then((graphData) => {
            return graphData;
        });
    }

    getDocuments(projectId: string, nodeIds: string[], page: number, limit: number): Promise<IDocumentResponse> {
        return this._detailApi.getDocuments(projectId, nodeIds, 'agent', page, limit).then((documents) => {
            return documents
        });
    }

    deleteDocument(projectId: string, documentId: string): Promise<void> {
        return this._SaveToProjectApi.removeFromProject({ business_id: projectId, business_node_id: documentId }).then(() => {
            this._message.success({
                message: this._i18nService.t('workProject.removeSuccess'),
            });
        });
    }

    getSeekQuestions(projectId: string, nodeId: string, nodeName: string): Promise<string[]> {
        return this._detailApi.getSeekQuestions(projectId, nodeId, nodeName).then((questions) => {
            return questions;
        });
    }

    getFiles(projectId: string, nodeIds: string[], page: number, limit: number): Promise<IFileResponse> {
        return this._detailApi.getDocuments(projectId, nodeIds, 'upload', page, limit).then((files) => {
            return files;
        });
    }

    deleteFile(projectId: string, fileId: string): Promise<void> {
        return this._SaveToProjectApi.removeFromProject({ business_id: projectId, business_node_id: fileId }).then(() => {
            this._message.success({
                message: this._i18nService.t('workProject.removeSuccess'),
            });
        });
    }

    batchDeleteFile(projectId: string, fileIds: string[]): Promise<void> {
        return this._SaveToProjectApi.batchRemoveFromProject({ business_id: projectId, business_node_ids: fileIds }).then(() => {
            this._message.success({
                message: this._i18nService.t('workProject.removeSuccess'),
            });
        });
    }

    editFile(projectId: string, fileId: string, fileName: string): Promise<void> {
        return this._detailApi.editFile(projectId, fileId, fileName).then(() => {
            this._message.success({
                message: this._i18nService.t('workProject.updateSuccess'),
            });
        });
    }

    refreshFile(projectId: string, fileId: string, retryType: 'agent' | 'upload', contentFormatType?: string): Promise<void> {
        return this._detailApi.refreshFile(projectId, fileId, retryType, contentFormatType);
    }

    getFilesStatus(projectId: string, fileIds: string[]): Promise<IFile[]> {
        return this._detailApi.getFilesStatus(projectId, fileIds).then((files) => {
            return files;
        });
    }

    uploadFile(projectId: string, file: File): Promise<{ item_id: string }> {
        return this._detailApi.uploadFile(projectId, file);
    }

    getExamples(projectId: string): Promise<string[]> {
        return this._detailApi.getExamples(projectId).then((examples) => {
            return examples;
        });
    }

    getPdfPath(itemId: string, projectId: string): Promise<string> {
        return this._detailApi.getPdfPath(itemId, projectId).then((pdfPath) => {
            return pdfPath;
        });
    }

    getLsDocumentAgentData(data: {business_id: string, business_type: 'ls_file' | 'ls_file_chat' | 'RD' | 'IP' | 'MSE' | 'LS', item_ids: string[], work_project_id: string}): Promise<{chat_id: string, file_id: string}> {
        return this._detailApi.getLsDocumentAgentData(data).then((data) => {
            return data;
        });
    }
}
